// Typed views over the game's data (stage 2 readability; generated by tools/accessors.mjs).
// Memory keeps the original layout: every property reads/writes the original address with the same width and
// signedness the original code used (e.g. sprite(choppers, i).state is R32/W32 at choppers + i*SPRITE.SIZE +
// SPRITE.state). Views are cached per address.
import { R16, R32, R8, RF64, W16, W32, W8, WF64 } from '../runtime/mem.js';
import { BULLETS_FIELD, DPLAY, HIGHSCORES_FIELD, IDEAL, MPLAY, SPRITE, aimAngle, allHerbDead, bombNext, bongBlowPending, bongHitTimer, bongShotsLeft, bongState, bullets, colorCyclePhase, currentWeapon, doubleBuffer, dusterSprayNext, explosionNext, explosionX, explosionY, fadeSteps, fireReady, frameCounter10, frameStartTime, gameState, hasAutoGun, hasBong, hasMissileLauncher, highScores, idleTimer, inLevelEndSequence, jahReplantX, jointGlowColor, keyboardState, kills, level, levelEndLoopState, levelEnding, levelTimer, masterVolume, missileSmokeNext, mouseButtons, mouseX, mouseY, musicSamplesEnabled, musicTrack, musicTrack0Data, musicTrack10Data, musicTrack1Data, musicTrack2Data, musicTrack3Data, musicTrack4Data, musicTrack5Data, musicTrack6Data, musicTrack7Data, musicTrack8Data, musicTrack9Data, musicVolume, paratrooperNext, prevGunSightX, prevGunSightY, randomVoice, rastaAnimDelay, resetHighScores, rightButtonHeld, score, sfxVolume, smokeGlowPhase, sndAutomaticData, sndBongBlowData, sndBongBubbleData, sndBongData, sndBongDeathData, sndChopperData, sndClickData, sndDoubleClickData, sndExplosionData, sndGameOverData, sndGetSomeData, sndGunShotData, sndIShotData, sndLogoData, sndMissileData, sndMusicSample10Data, sndMusicSample1Data, sndMusicSample2Data, sndMusicSample3Data, sndMusicSample4Data, sndMusicSample5Data, sndMusicSample65Data, sndMusicSample6Data, sndMusicSample7Data, sndMusicSample8Data, sndMusicSample9Data, sndNukeData, sndParaDie1Data, sndParaDie2Data, sndParaDie3Data, sndParaDie5Data, sndParaSquishData, sndPdie4Data, sndProtectData, sndRastaRocketData, sndRicochetData, sndSmokinData, sndUfo2Data, sndUfoData, sndYaMonData, songStatus, soundStatus, ufoSoundStatus, ufoTargetPlant } from './data.js';

// An object whose properties read/write memory: props = { name: [offset, read, write] } relative to a.
function view(a, props) {
  const o = { addr: a };
  for (const [name, [off, read, write]] of Object.entries(props)) {
    const p = a + off;
    Object.defineProperty(o, name, { get: () => read(p), set: (v) => write(p, v), enumerable: true });
  }
  return o;
}

const SPRITE_PROPS = {
  x: [SPRITE.x, R32, W32],
  y: [SPRITE.y, R32, W32],
  currFrame: [SPRITE.currFrame, R32, W32],
  threshold1: [SPRITE.threshold1, R32, W32],
  threshold2: [SPRITE.threshold2, R32, W32],
  width: [SPRITE.width, R32, W32],
  height: [SPRITE.height, R32, W32],
  visible: [SPRITE.visible, R32, W32],
  state: [SPRITE.state, R32, W32],
  counter2: [SPRITE.counter2, R32, W32],
  counter1: [SPRITE.counter1, R32, W32],
  counter3: [SPRITE.counter3, R32, W32],
  threshold3: [SPRITE.threshold3, R32, W32],
  numFrames: [SPRITE.numFrames, R32, W32],
};
const spriteViews = new Map();
export function sprite(base, i = 0) {
  const a = base + i * SPRITE.SIZE;
  return spriteViews.get(a) ?? spriteViews.set(a, view(a, SPRITE_PROPS)).get(a);
}

const MPLAY_PROPS = {
  count: [MPLAY.count, R16, W16],
  track: [MPLAY.track, R32, W32],
};
const mplayViews = new Map();
export function mplay(base, i = 0) {
  const a = base + i * MPLAY.SIZE;
  return mplayViews.get(a) ?? mplayViews.set(a, view(a, MPLAY_PROPS)).get(a);
}

const DPLAY_PROPS = {
  soundnum: [DPLAY.soundnum, R16, W16],
  count: [DPLAY.count, R16, W16],
  priority: [DPLAY.priority, R16, W16],
  presnd: [DPLAY.presnd, R16, W16],
  snd: [DPLAY.snd, R32, W32],
};
const dplayViews = new Map();
export function dplay(base, i = 0) {
  const a = base + i * DPLAY.SIZE;
  return dplayViews.get(a) ?? dplayViews.set(a, view(a, DPLAY_PROPS)).get(a);
}

const IDEAL_PROPS = {
  musicType: [IDEAL.musicType, R16, W16],
  digitalType: [IDEAL.digitalType, R16, W16],
  digitalRate: [IDEAL.digitalRate, R16, W16],
  digitalVoices: [IDEAL.digitalVoices, R16, W16],
  field8: [IDEAL.field8, R16, W16],
};
const idealViews = new Map();
export function ideal(base, i = 0) {
  const a = base + i * IDEAL.SIZE;
  return idealViews.get(a) ?? idealViews.set(a, view(a, IDEAL_PROPS)).get(a);
}

const BULLET_PROPS = {
  y: [BULLETS_FIELD.y, R32, W32],
  x: [BULLETS_FIELD.x, R32, W32],
  savedPixel: [BULLETS_FIELD.savedPixel, R32, W32],
  active: [BULLETS_FIELD.active, R32, W32],
  xStepTenths: [BULLETS_FIELD.xStepTenths, R32, W32],
  yStepTenths: [BULLETS_FIELD.yStepTenths, R32, W32],
  yStepCounter: [BULLETS_FIELD.yStepCounter, R32, W32],
  xStepCounter: [BULLETS_FIELD.xStepCounter, R32, W32],
};
const bulletViews = new Map();
export function bullet(i) {
  const a = bullets + i * BULLETS_FIELD.SIZE;
  return bulletViews.get(a) ?? bulletViews.set(a, view(a, BULLET_PROPS)).get(a);
}

const HIGHSCORE_PROPS = {
  score: [HIGHSCORES_FIELD.score, R32, W32],
  level: [HIGHSCORES_FIELD.level, R32, W32],
};
const highScoreViews = new Map();
export function highScore(i) {
  const a = highScores + i * HIGHSCORES_FIELD.SIZE;
  return highScoreViews.get(a) ?? highScoreViews.set(a, view(a, HIGHSCORE_PROPS)).get(a);
}

// Scalar globals: G.score, G.level, ...
export const G = view(0, {
  aimAngle: [aimAngle, RF64, WF64],
  allHerbDead: [allHerbDead, R32, W32],
  bombNext: [bombNext, R32, W32],
  bongBlowPending: [bongBlowPending, R32, W32],
  bongHitTimer: [bongHitTimer, R32, W32],
  bongShotsLeft: [bongShotsLeft, R32, W32],
  bongState: [bongState, R32, W32],
  colorCyclePhase: [colorCyclePhase, R32, W32],
  currentWeapon: [currentWeapon, R32, W32],
  doubleBuffer: [doubleBuffer, R32, W32],
  dusterSprayNext: [dusterSprayNext, R32, W32],
  explosionNext: [explosionNext, R32, W32],
  explosionX: [explosionX, R32, W32],
  explosionY: [explosionY, R32, W32],
  fadeSteps: [fadeSteps, RF64, WF64],
  fireReady: [fireReady, R32, W32],
  frameCounter10: [frameCounter10, R32, W32],
  frameStartTime: [frameStartTime, R32, W32],
  gameState: [gameState, R32, W32],
  hasAutoGun: [hasAutoGun, R32, W32],
  hasBong: [hasBong, R32, W32],
  hasMissileLauncher: [hasMissileLauncher, R32, W32],
  idleTimer: [idleTimer, R32, W32],
  inLevelEndSequence: [inLevelEndSequence, R32, W32],
  jahReplantX: [jahReplantX, R32, W32],
  jointGlowColor: [jointGlowColor, R8, W8],
  kills: [kills, R32, W32],
  level: [level, R32, W32],
  levelEndLoopState: [levelEndLoopState, R32, W32],
  levelEnding: [levelEnding, R32, W32],
  levelTimer: [levelTimer, R32, W32],
  masterVolume: [masterVolume, R16, W16],
  missileSmokeNext: [missileSmokeNext, R32, W32],
  mouseButtons: [mouseButtons, R32, W32],
  mouseX: [mouseX, R32, W32],
  mouseY: [mouseY, R32, W32],
  musicSamplesEnabled: [musicSamplesEnabled, R32, W32],
  musicTrack: [musicTrack, R32, W32],
  musicTrack0Data: [musicTrack0Data, R32, W32],
  musicTrack10Data: [musicTrack10Data, R32, W32],
  musicTrack1Data: [musicTrack1Data, R32, W32],
  musicTrack2Data: [musicTrack2Data, R32, W32],
  musicTrack3Data: [musicTrack3Data, R32, W32],
  musicTrack4Data: [musicTrack4Data, R32, W32],
  musicTrack5Data: [musicTrack5Data, R32, W32],
  musicTrack6Data: [musicTrack6Data, R32, W32],
  musicTrack7Data: [musicTrack7Data, R32, W32],
  musicTrack8Data: [musicTrack8Data, R32, W32],
  musicTrack9Data: [musicTrack9Data, R32, W32],
  musicVolume: [musicVolume, R16, W16],
  paratrooperNext: [paratrooperNext, R32, W32],
  prevGunSightX: [prevGunSightX, R32, W32],
  prevGunSightY: [prevGunSightY, R32, W32],
  randomVoice: [randomVoice, R32, W32],
  rastaAnimDelay: [rastaAnimDelay, R32, W32],
  resetHighScores: [resetHighScores, R32, W32],
  rightButtonHeld: [rightButtonHeld, R32, W32],
  score: [score, R32, W32],
  sfxVolume: [sfxVolume, R16, W16],
  smokeGlowPhase: [smokeGlowPhase, R32, W32],
  sndAutomaticData: [sndAutomaticData, R32, W32],
  sndBongBlowData: [sndBongBlowData, R32, W32],
  sndBongBubbleData: [sndBongBubbleData, R32, W32],
  sndBongData: [sndBongData, R32, W32],
  sndBongDeathData: [sndBongDeathData, R32, W32],
  sndChopperData: [sndChopperData, R32, W32],
  sndClickData: [sndClickData, R32, W32],
  sndDoubleClickData: [sndDoubleClickData, R32, W32],
  sndExplosionData: [sndExplosionData, R32, W32],
  sndGameOverData: [sndGameOverData, R32, W32],
  sndGetSomeData: [sndGetSomeData, R32, W32],
  sndGunShotData: [sndGunShotData, R32, W32],
  sndIShotData: [sndIShotData, R32, W32],
  sndLogoData: [sndLogoData, R32, W32],
  sndMissileData: [sndMissileData, R32, W32],
  sndMusicSample10Data: [sndMusicSample10Data, R32, W32],
  sndMusicSample1Data: [sndMusicSample1Data, R32, W32],
  sndMusicSample2Data: [sndMusicSample2Data, R32, W32],
  sndMusicSample3Data: [sndMusicSample3Data, R32, W32],
  sndMusicSample4Data: [sndMusicSample4Data, R32, W32],
  sndMusicSample5Data: [sndMusicSample5Data, R32, W32],
  sndMusicSample65Data: [sndMusicSample65Data, R32, W32],
  sndMusicSample6Data: [sndMusicSample6Data, R32, W32],
  sndMusicSample7Data: [sndMusicSample7Data, R32, W32],
  sndMusicSample8Data: [sndMusicSample8Data, R32, W32],
  sndMusicSample9Data: [sndMusicSample9Data, R32, W32],
  sndNukeData: [sndNukeData, R32, W32],
  sndParaDie1Data: [sndParaDie1Data, R32, W32],
  sndParaDie2Data: [sndParaDie2Data, R32, W32],
  sndParaDie3Data: [sndParaDie3Data, R32, W32],
  sndParaDie5Data: [sndParaDie5Data, R32, W32],
  sndParaSquishData: [sndParaSquishData, R32, W32],
  sndPdie4Data: [sndPdie4Data, R32, W32],
  sndProtectData: [sndProtectData, R32, W32],
  sndRastaRocketData: [sndRastaRocketData, R32, W32],
  sndRicochetData: [sndRicochetData, R32, W32],
  sndSmokinData: [sndSmokinData, R32, W32],
  sndUfo2Data: [sndUfo2Data, R32, W32],
  sndUfoData: [sndUfoData, R32, W32],
  sndYaMonData: [sndYaMonData, R32, W32],
  songStatus: [songStatus, R8, W8],
  soundStatus: [soundStatus, R16, W16],
  ufoSoundStatus: [ufoSoundStatus, R16, W16],
  ufoTargetPlant: [ufoTargetPlant, R32, W32],
});

// keyboard_state[scan] (the library keyboard driver's key table): 1 while the key is held.
export const keyDown = (scan) => R32(keyboardState + 4 * scan);
